import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../redux/helper';
import {
  completeActivationTask,
  getMergedActivationTasks,
  selectActivationCompletedCount,
  selectActivationTracker,
  selectActivationTrackerPhase,
  selectOwnerActivation,
  type ActivationCompletionSource,
  type ActivationLocationType,
  type ActivationTaskId,
} from '../redux/ownerActivationSlice';
import ownerActivationAnalytics from '../services/ownerActivationAnalytics';
import type { OwnerActivationAnalyticsPlatform } from '../services/ownerActivationAnalyticsTypes';

/** Build a session-dedupe key for bootstrap evidence. */
const buildBootstrapKey = (
  taskId: ActivationTaskId,
  locationId: string | null,
): string => {
  if (taskId === 'visit_test_location') {
    return taskId;
  }
  return locationId ? `${locationId}:${taskId}` : taskId;
};

/**
 * Bootstrap reconciliation of activation tasks from verified existing data
 * (contract: specs/001-new-owner-setup-wizard/contracts/activation-events.md
 * §4). Monotonic: can complete a task, never uncompletes one. Evidence must
 * come from a successful fetch or known current-location resolution — never
 * from loading or error states, which callers must not report at all.
 */

export type OwnerActivationBootstrapEvidence =
  | {
      taskId: 'add_product';
      verifiedAt: string;
      hasRealLocationProduct: true;
      locationId: string;
    }
  | {
      taskId: 'first_sale';
      verifiedAt: string;
      hasCompletedRealLocationSale: true;
      locationId: string;
    }
  | {
      taskId: 'add_staff';
      verifiedAt: string;
      hasNonOwnerEmployee: true;
      /** Required non-empty string for location-scoped task. */
      locationId: string;
    }
  | {
      taskId: 'visit_test_location';
      verifiedAt: string;
      currentLocationIsTest: true;
      locationId: string;
    };

export type BootstrapCompletionPayload = {
  taskId: ActivationTaskId;
  completedAt: string;
  completionSource: ActivationCompletionSource;
  locationId: string | null;
  locationType: ActivationLocationType | null;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * Validates one evidence item and maps it to a completion payload, or null
 * when the evidence is not sufficient. Back Office exploration is never
 * bootstrapped (there is no valid evidence shape for it).
 */
export const resolveBootstrapCompletion = (
  evidence: OwnerActivationBootstrapEvidence,
): BootstrapCompletionPayload | null => {
  if (!evidence || !isNonEmptyString((evidence as any).verifiedAt)) {
    return null;
  }
  const base = {
    completedAt: evidence.verifiedAt,
    completionSource: 'bootstrap_existing_data' as const,
  };
  switch (evidence.taskId) {
    case 'add_product':
      if (
        evidence.hasRealLocationProduct !== true ||
        !isNonEmptyString(evidence.locationId)
      ) {
        return null;
      }
      return {
        ...base,
        taskId: 'add_product',
        locationId: evidence.locationId,
        locationType: 'real',
      };
    case 'first_sale':
      if (
        evidence.hasCompletedRealLocationSale !== true ||
        !isNonEmptyString(evidence.locationId)
      ) {
        return null;
      }
      return {
        ...base,
        taskId: 'first_sale',
        locationId: evidence.locationId,
        locationType: 'real',
      };
    case 'add_staff':
      if (evidence.hasNonOwnerEmployee !== true) {
        return null;
      }
      // Require non-empty locationId for location-scoped task.
      if (!isNonEmptyString(evidence.locationId)) {
        return null;
      }
      return {
        ...base,
        taskId: 'add_staff',
        locationId: evidence.locationId,
        locationType: 'real',
      };
    case 'visit_test_location':
      if (
        evidence.currentLocationIsTest !== true ||
        !isNonEmptyString(evidence.locationId)
      ) {
        return null;
      }
      return {
        ...base,
        taskId: 'visit_test_location',
        locationId: evidence.locationId,
        locationType: 'test',
      };
    default:
      // Unknown task (including explore_backoffice, which is never
      // bootstrapped from auth/session state).
      return null;
  }
};

export const useOwnerActivationBootstrap = () => {
  const dispatch = useDispatch();
  const tracker = useAppSelector(selectActivationTracker);
  const phase = useAppSelector(selectActivationTrackerPhase);
  const completedCount = useAppSelector(selectActivationCompletedCount);
  const owner = useAppSelector(selectOwnerActivation);
  const businessType = useAppSelector(
    state => state.auth?.inApp?.account?.businessInfo?.type,
  );
  const trackerRef = useRef(tracker);
  useEffect(() => {
    trackerRef.current = tracker;
  }, [tracker]);
  // Suppress repeated dispatch of the same task within this mount (e.g. Home
  // focus cycles); the reducer is idempotent regardless.
  // Keys are location-scoped for location tasks, global for visit_test_location.
  const reportedKeysRef = useRef<Set<string>>(new Set());

  const reportBootstrapEvidence = useCallback(
    (evidenceList: OwnerActivationBootstrapEvidence[]) => {
      for (const evidence of evidenceList) {
        const payload = resolveBootstrapCompletion(evidence);
        if (!payload) continue;
        // Check completion by looking at the target location's bucket.
        const tasks = getMergedActivationTasks(
          trackerRef.current,
          payload.locationId,
        );
        if (tasks[payload.taskId]?.completedAt !== null) continue;
        const key = buildBootstrapKey(payload.taskId, payload.locationId);
        if (reportedKeysRef.current.has(key)) continue;
        reportedKeysRef.current.add(key);
        dispatch(completeActivationTask(payload));
        try {
          const platform: OwnerActivationAnalyticsPlatform =
            Platform.OS === 'ios'
              ? 'ios'
              : Platform.OS === 'android'
                ? 'android'
                : 'web';
          ownerActivationAnalytics.track('activation_task_bootstrapped', {
            onboarding_version: 1,
            platform,
            business_type_group: businessType || undefined,
            tracker_phase: phase === 'inactive' ? 'active' : phase,
            completed_task_count: completedCount + 1,
            wizard_product_created: owner.wizard.productCreatedDuringWizard,
            task_id: payload.taskId,
            completion_source: 'bootstrap_existing_data',
          });
        } catch {
          // ignore
        }
      }
    },
    [
      businessType,
      completedCount,
      dispatch,
      owner.wizard.productCreatedDuringWizard,
      phase,
    ],
  );

  return { reportBootstrapEvidence };
};

export default useOwnerActivationBootstrap;
